import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { commandCenterApi } from '../services/api'
import styles from './KambiOutcomeSelector.module.css'

const MIN_SEARCH_LENGTH = 2
const SEARCH_LIMIT = 25

function providerSymbol(selection) {
  const meta = selection.selection_metadata || {}
  return meta.symbol || selection.external_id || ''
}

function eventLabel(selection) {
  const meta = selection.selection_metadata || {}
  return selection.event_name || meta.event_name || 'Event'
}

function directionLabel(selection) {
  const meta = selection.selection_metadata || {}
  return meta.team_abbrev || meta.team_name || selection.name || 'Contract'
}

function marketLabel(selection) {
  const meta = selection.selection_metadata || {}
  return selection.market_name || meta.market_title || 'Market'
}

function percentLabel(selection) {
  const meta = selection.selection_metadata || {}
  const chance = Number(meta.chance)
  if (Number.isFinite(chance)) {
    return `${Math.round(chance)}%`
  }

  const yesPrice = Number(meta.yes_price)
  if (Number.isFinite(yesPrice)) {
    return `${Math.round(yesPrice * 100)}%`
  }

  return '-'
}

function contractLabel(selection) {
  return `${eventLabel(selection)} - Buy ${directionLabel(selection)} - ${percentLabel(selection)}`
}

function contractCacheFromSelections(selections) {
  return Object.fromEntries(
    selections
      .filter((selection) => selection.external_id)
      .map((selection) => [selection.external_id, selection]),
  )
}

export default function CryptoDotComContractSelector({
  orgSlug,
  providerSelectionIds,
  onProviderSelectionIdsChange,
  onSelectedContractsChange,
}) {
  const [query, setQuery] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [contractCache, setContractCache] = useState({})
  const trimmedQuery = query.trim()

  useEffect(() => {
    if (trimmedQuery.length < MIN_SEARCH_LENGTH) {
      setSearchQuery('')
      return undefined
    }

    const handle = window.setTimeout(() => {
      setSearchQuery(trimmedQuery)
    }, 250)

    return () => window.clearTimeout(handle)
  }, [trimmedQuery])

  const { data, isFetching, error } = useQuery({
    queryKey: ['cryptodotcom-contracts', orgSlug, searchQuery],
    queryFn: () => commandCenterApi.listOrgSelections(orgSlug, { q: searchQuery, limit: SEARCH_LIMIT }),
    enabled: !!orgSlug && searchQuery.length >= MIN_SEARCH_LENGTH,
  })

  const selections = useMemo(
    () => (searchQuery.length >= MIN_SEARCH_LENGTH ? data?.selections || [] : []),
    [data?.selections, searchQuery],
  )

  useEffect(() => {
    if (selections.length === 0) return
    setContractCache((previousValue) => ({
      ...previousValue,
      ...contractCacheFromSelections(selections),
    }))
  }, [selections])

  const selectedContracts = useMemo(
    () => providerSelectionIds.map((id) => contractCache[id]).filter(Boolean),
    [providerSelectionIds, contractCache],
  )

  const selectedByExternalID = new Set(providerSelectionIds)

  const notifySelection = (nextIds, nextCache = contractCache) => {
    onProviderSelectionIdsChange(nextIds)
    const nextContracts = nextIds.map((id) => nextCache[id]).filter(Boolean)
    onSelectedContractsChange?.(nextContracts)
  }

  const toggleContract = (selection) => {
    const externalID = selection.external_id
    if (!externalID) return
    const nextCache = { ...contractCache, [externalID]: selection }
    setContractCache(nextCache)
    const nextIds = selectedByExternalID.has(externalID)
      ? providerSelectionIds.filter((id) => id !== externalID)
      : [externalID]
    notifySelection(nextIds, nextCache)
  }

  const removeContract = (externalID) => {
    notifySelection(providerSelectionIds.filter((id) => id !== externalID))
  }

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        <label className={styles.sectionLabel}>Crypto.com Contracts</label>
        <div className={styles.inputRow}>
          <input
            className={styles.outcomeInput}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search event, team, contract, or symbol"
          />
        </div>

        {providerSelectionIds.length > 0 ? (
          <div className={styles.chipList}>
            {providerSelectionIds.map((id) => {
              const selection = selectedContracts.find((item) => item.external_id === id)
              return (
                <span key={id} className={`${styles.chip} ${styles.cryptoChip}`}>
                  {selection ? contractLabel(selection) : id}
                  <button type="button" className={styles.chipRemove} onClick={() => removeContract(id)}>
                    &times;
                  </button>
                </span>
              )
            })}
          </div>
        ) : null}
      </div>

      {isFetching ? <div className={styles.validatingMsg}>Loading contracts...</div> : null}
      {error ? <div className={styles.errorMsg}>{error.response?.data?.error || 'Failed to load Crypto.com contracts'}</div> : null}
      {searchQuery.length >= MIN_SEARCH_LENGTH && !isFetching && !error && selections.length === 0 ? (
        <div className={styles.validatingMsg}>No matching contracts.</div>
      ) : null}

      {selections.length > 0 ? (
        <div className={`${styles.validationPreview} ${styles.cryptoContractResults}`}>
          <div className={`${styles.eventCard} ${styles.cryptoContractCard}`}>
            {selections.map((selection) => {
              const selected = selectedByExternalID.has(selection.external_id)
              const symbol = providerSymbol(selection)
              const direction = directionLabel(selection)
              const market = marketLabel(selection)
              const percent = percentLabel(selection)
              return (
                <button
                  key={selection.id}
                  type="button"
                  className={`${styles.outcomeRow} ${styles.cryptoContractRow}`}
                  onClick={() => toggleContract(selection)}
                  aria-pressed={selected}
                  aria-label={`${eventLabel(selection)} Buy ${direction} ${market} ${percent}`}
                >
                  <div className={styles.cryptoContractSummary}>
                    <div className={styles.cryptoContractInfo}>
                      <div className={styles.cryptoEventName}>{eventLabel(selection)}</div>
                      <div className={styles.cryptoBetLine}>
                        <span className={styles.cryptoDirection}>Buy {direction}</span>
                        <span className={styles.cryptoMarket}>{market}</span>
                      </div>
                    </div>
                    <div className={styles.cryptoPercent}>{percent}</div>
                  </div>
                  <div className={styles.cryptoContractMeta}>
                    {symbol ? <span title={symbol}>{symbol}</span> : null}
                    {selected ? <span className={styles.betOfferType}>Selected</span> : null}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}
